import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { getCurrentSession, signOut, PORTAL_ROUTE_BY_GROUP } from '../auth/cognito';

const SESSION_CHECK_INTERVAL_MS = 4 * 60 * 1000; // 4 min — well within Cognito's 1-hr access token

// Wraps portal routes. Redirects to /login when the visitor has no valid
// Cognito session, or to their own portal when they're signed in but landing
// on the wrong role's portal.
export default function RequireAuth({ role, children }) {
  const location = useLocation();
  const [state, setState] = useState({ status: 'loading', user: null });

  // Initial session check on mount.
  useEffect(() => {
    let cancelled = false;
    getCurrentSession().then((user) => {
      if (cancelled) return;
      setState({ status: user ? 'authed' : 'anon', user });
    });
    return () => { cancelled = true; };
  }, []);

  // Periodic refresh: if the Cognito refresh token has expired, getSession()
  // inside getCurrentSession() will fail and return null — hard-logout here
  // so portal API calls never silently fail with 401s.
  useEffect(() => {
    if (state.status !== 'authed') return;
    const id = setInterval(async () => {
      const user = await getCurrentSession();
      if (!user) {
        signOut();
        setState({ status: 'anon', user: null });
      }
    }, SESSION_CHECK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [state.status]);

  if (state.status === 'loading') return null;

  if (state.status === 'anon') {
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  if (state.user.group !== role) {
    const target = PORTAL_ROUTE_BY_GROUP[state.user.group] || '/';
    return <Navigate to={target} replace />;
  }

  return typeof children === 'function' ? children(state.user) : children;
}
